import { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { servicesData } from "@/pages/services/data";

function ServiceCard({ service, index }: { service: typeof servicesData[0]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.1 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="transition-all duration-700"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(32px)",
        transitionDelay: `${index * 70}ms`,
      }}
    >
      <Link
        to={`/services/${service.slug}`}
        className="group flex flex-col bg-white rounded-xl border border-gray-100 hover:border-cyan-200 overflow-hidden h-full transition-all hover:-translate-y-1 cursor-pointer"
        style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}
      >
        {/* Coloured header bar */}
        <div className={`h-2 w-full bg-gradient-to-r ${service.accentFrom} ${service.accentTo}`}></div>

        {/* Service photo */}
        <div className="relative aspect-[2/1] overflow-hidden bg-gray-100">
          <img
            src={`/images/services-grid/${service.slug}.webp`}
            alt={service.title}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>

        <div className="p-7 flex flex-col flex-1">
          {/* Icon + number */}
          <div className="flex items-start justify-between mb-5">
            <div className={`w-12 h-12 flex items-center justify-center rounded-xl bg-gradient-to-br ${service.accentFrom} ${service.accentTo} shrink-0`}>
              <i className={`${service.icon} text-white text-xl`}></i>
            </div>
            <span className="text-gray-200 text-4xl font-extrabold leading-none select-none">
              {String(index + 1).padStart(2, "0")}
            </span>
          </div>

          {/* Title */}
          <h3 className="text-gray-900 font-bold text-base mb-1 group-hover:text-cyan-700 transition-colors leading-snug">
            {service.shortTitle}
          </h3>
          <p className="text-cyan-600 text-xs font-medium mb-4 leading-snug">{service.subtitle}</p>

          {/* Overview */}
          <p className="text-gray-500 text-xs leading-relaxed flex-1 line-clamp-3">{service.overview}</p>

          {/* Scope preview pills */}
          <div className="flex flex-wrap gap-1.5 mt-5">
            {service.scope.slice(0, 3).map((s) => (
              <span key={s.title} className="bg-cyan-50 text-cyan-700 text-[10px] font-semibold px-2 py-0.5 rounded-full">
                {s.title}
              </span>
            ))}
            {service.scope.length > 3 && (
              <span className="bg-gray-50 text-gray-400 text-[10px] font-semibold px-2 py-0.5 rounded-full">
                +{service.scope.length - 3} more
              </span>
            )}
          </div>

          {/* CTA */}
          <div className="flex items-center gap-1.5 mt-5 text-cyan-600 text-xs font-semibold group-hover:gap-3 transition-all">
            View Full Details <i className="ri-arrow-right-line"></i>
          </div>
        </div>
      </Link>
    </div>
  );
}

export default function ServicesGrid() {
  return (
    <section id="services-grid" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="inline-block bg-cyan-50 text-cyan-600 text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full">
            What We Do
          </span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mt-4">
            Choose a Service to Learn More
          </h2>
          <p className="text-gray-500 text-sm mt-3 max-w-xl mx-auto leading-relaxed">
            Each service is backed by a dedicated team of engineers, full project documentation
            and ongoing support — click any service card for the complete details.
          </p>
        </div>

        {/* 7 Cards — 3 cols on large, 2 on md, 1 on mobile */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {servicesData.map((service, i) => (
            <ServiceCard key={service.slug} service={service} index={i} />
          ))}
        </div>

        {/* CTA Banner */}
        <div className="mt-16 rounded-2xl overflow-hidden relative">
          <img
            src="/images/kitchen/installation-team.webp"
            alt="ISMAN Engineering Team"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-950/95 via-cyan-900/90 to-teal-900/80"></div>
          <div className="relative px-8 py-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <p className="text-cyan-300 text-xs uppercase tracking-widest font-semibold mb-2">
                Not Sure Which Service You Need?
              </p>
              <h3 className="text-white text-2xl md:text-3xl font-extrabold">
                Talk to an ISMAN Engineer Today
              </h3>
              <p className="text-cyan-100/70 text-sm mt-2 max-w-md leading-relaxed">
                Our team will assess your requirements and recommend the right engineering solution
                for your facility — no obligation, no pressure.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 shrink-0">
              <a
                href="tel:0724114555"
                className="bg-cyan-600 hover:bg-cyan-500 text-white font-bold px-7 py-3 rounded-md text-sm uppercase tracking-wider transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-phone-line"></i> Call Us Now
              </a>
              <Link
                to="/#contact"
                className="border border-white/40 hover:border-cyan-400 hover:text-cyan-300 text-white font-bold px-7 py-3 rounded-md text-sm uppercase tracking-wider transition-colors cursor-pointer whitespace-nowrap text-center"
              >
                Send Enquiry
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
